import { Box } from "@mui/material";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep, isEqual } from "lodash";
import { Prompt } from "react-router";
import { forwardRef, useImperativeHandle } from "react";
import { RootState } from "../../../store";
import ChildrenLabTestEdit from "./ChildrenLabTestEdit";
import TestGroupNameAndCategoryEdit from "./TestGroupNameAndCategoryEdit";
import * as NotificationActions from "../../../actions/notification";
import { postTestGroup, setInitialState } from "../../../slices/labTestSettingsSlice";
import { DocumentRecordGroup } from "../../../interfaces/Lab";
import LabGroupAdditionalSettingsEdit from "./LabGroupAdditionalSettingsEdit";

const isEqualState = (
  finalState: DocumentRecordGroup,
  initialState: DocumentRecordGroup
): boolean => {
  if (finalState === null && initialState === null) return true;
  const formattedFinal = cloneDeep(finalState);
  const removeUUIDS = (testsArray) => {
    testsArray?.forEach((test) => {
      // eslint-disable-next-line no-param-reassign
      delete test.uuid;
      if (test.subTests?.length > 0) {
        removeUUIDS(test.subTests);
      }
    });
  };
  removeUUIDS(formattedFinal.labTests);
  return isEqual(formattedFinal, initialState);
};

export interface EditScreenHandle {
  handleSave: () => Promise<void>;
}

const EditScreen = forwardRef(
  (
    { onCancel }: { onCancel: () => void },
    ref: React.Ref<EditScreenHandle>
  ): React.JSX.Element | null => {
    const draftGroup = useSelector((state: RootState) => state.labSettings.draftGroup);
    const initialState = useSelector((state: RootState) => state.labSettings.initState);
    const dispatch = useDispatch();

    useImperativeHandle(ref, () => ({
      async handleSave() {
        try {
          dispatch(setInitialState(draftGroup));
          await dispatch(postTestGroup(draftGroup));
          onCancel();
          dispatch(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "success",
              message: "Succesfully Updated Testgroup!",
              autoTimeout: true
            })
          );
        } catch (e) {
          onCancel();
          dispatch(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: "Something went wrong! Failed To Update Testgroup!",
              autoTimeout: true
            })
          );
        }
      }
    }));

    if (!draftGroup) return null;

    return (
      <div>
        <Prompt
          when={!isEqualState(draftGroup, initialState)}
          message={() => `You have unsaved changes!\nAre you sure you want to leave this page?`}
        />
        <Box>
          <TestGroupNameAndCategoryEdit />
          <ChildrenLabTestEdit />
          <LabGroupAdditionalSettingsEdit />
        </Box>
      </div>
    );
  }
);

export default EditScreen;
